import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { X, Play, Pause, ChevronRight, ChevronLeft, Clapperboard } from 'lucide-react';
import { toast } from 'sonner';
import { generateChampionVideo, shareOrDownloadVideo } from '../utils/championVideo';

const ROUND_ORDER = [
  'group_a', 'group_b', 'group_c', 'group_d', 'group_stage',
  'round_of_32', 'round_of_16', 'quarter_final', 'semi_final', 'third_place',
  'consolation_r16', 'consolation_quarter', 'consolation_semi', 'consolation_final',
  'final',
];

const ROUND_TITLES = {
  group_a: 'בית א׳', group_b: 'בית ב׳', group_c: 'בית ג׳', group_d: 'בית ד׳',
  group_stage: 'שלב הבתים', round_of_32: 'סיבוב 32', round_of_16: 'שמינית גמר',
  quarter_final: 'רבע גמר', semi_final: 'חצי גמר', third_place: 'משחק על מקום 3',
  consolation_r16: 'תנחומים — שמינית', consolation_quarter: 'תנחומים — רבע גמר',
  consolation_semi: 'תנחומים — חצי גמר', consolation_final: 'גמר התנחומים',
  final: '🏆 הגמר הגדול 🏆',
};

const SLIDE_MS = 5000;
const MAX_PER_SLIDE = 4;

const TournamentReplay = ({ open, onClose, matches = [], players = [], tournamentName, tournamentDate, condensed = false, onComplete }) => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [videoProgress, setVideoProgress] = useState(null);

  const handleShareVideo = async (slide) => {
    if (videoProgress !== null) return;
    setPlaying(false);
    setVideoProgress(0);
    try {
      const dateStr = tournamentDate ? new Date(tournamentDate).toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
      const blob = await generateChampionVideo(
        { tournamentName, dateStr, champName: slide.champName, runnerName: slide.runnerName, consChamp: slide.consChamp },
        setVideoProgress
      );
      const res = await shareOrDownloadVideo(blob, {
        filename: `champion-${(slide.champName || 'tennis').replace(/\s+/g, '-')}`,
        title: `🏆 ${slide.champName} - אלוף ${tournamentName}`,
      });
      if (res === 'shared') toast.success('הסרטון שותף! 🎬');
      else if (res === 'downloaded') toast.success('הסרטון הורד - שתף אותו בוואטסאפ/אינסטגרם 🎾');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'שגיאה ביצירת הסרטון');
    } finally {
      setVideoProgress(null);
    }
  };

  const playerMap = useMemo(() => {
    const map = {};
    (players || []).forEach((p) => { if (p?.id) map[p.id] = p; });
    return map;
  }, [players]);

  const slides = useMemo(() => {
    const completed = matches.filter((m) => m.completed && m.winner_id);
    const CONDENSED_ROUNDS = ['quarter_final', 'semi_final', 'third_place', 'consolation_final', 'final'];
    let roundOrder = ROUND_ORDER;
    if (condensed && completed.some((m) => CONDENSED_ROUNDS.includes(m.round))) {
      roundOrder = ROUND_ORDER.filter((r) => CONDENSED_ROUNDS.includes(r));
    }
    const result = [{ type: 'intro' }];
    roundOrder.forEach((round) => {
      const roundMatches = completed
        .filter((m) => m.round === round || (round === 'group_stage' && !m.round && m.group_name))
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      for (let i = 0; i < roundMatches.length; i += MAX_PER_SLIDE) {
        result.push({
          type: 'round',
          round,
          title: ROUND_TITLES[round] || round,
          part: roundMatches.length > MAX_PER_SLIDE ? Math.floor(i / MAX_PER_SLIDE) + 1 : null,
          matches: roundMatches.slice(i, i + MAX_PER_SLIDE),
        });
      }
    });
    const finalMatch = completed.find((m) => m.round === 'final');
    const consFinal = completed.find((m) => m.round === 'consolation_final');
    if (finalMatch) {
      const champName = finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player1_name : finalMatch.player2_name;
      const runnerName = finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player2_name : finalMatch.player1_name;
      const consChamp = consFinal ? (consFinal.winner_id === consFinal.player1_id ? consFinal.player1_name : consFinal.player2_name) : null;
      result.push({ type: 'champion', champId: finalMatch.winner_id, champName, runnerName, consChamp });
    }
    return result;
  }, [matches]);

  const total = slides.length;

  useEffect(() => {
    if (open) { setIdx(0); setPlaying(true); }
  }, [open]);

  useEffect(() => {
    if (!open || !playing) return;
    if (idx >= total - 1) {
      if (onComplete) {
        const t = setTimeout(onComplete, SLIDE_MS);
        return () => clearTimeout(t);
      }
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setIdx((i) => Math.min(i + 1, total - 1)), SLIDE_MS);
    return () => clearTimeout(t);
  }, [open, playing, idx, total, onComplete]);

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') next();
      if (e.key === 'ArrowRight') prev();
      if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, next, prev, onClose]);

  if (!open) return null;

  const slide = slides[idx];

  const PlayerAvatar = ({ id, name, size = 'w-10 h-10' }) => {
    const p = playerMap[id];
    return (
      <Avatar className={`${size} border-2 border-amber-400/60 shrink-0`}>
        {p?.avatar && <AvatarImage src={p.avatar} alt={name} />}
        <AvatarFallback className="bg-slate-700 text-amber-300 font-bold text-sm">{(name || '?').charAt(0)}</AvatarFallback>
      </Avatar>
    );
  };

  const MatchRow = ({ m, delay }) => {
    const p1win = m.winner_id === m.player1_id;
    const special = m.win_type === 'walkover' ? 'W.O.' : m.win_type === 'retirement' ? 'פרישה' : null;
    const Row = ({ id, name, win, side }) => (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${win ? 'bg-emerald-500/15' : ''}`}>
        <PlayerAvatar id={id} name={name} size="w-8 h-8" />
        {win && <span className="shrink-0">🏆</span>}
        <span className={`flex-1 min-w-0 truncate text-sm ${win ? 'font-extrabold text-emerald-300' : 'text-slate-400'}`}>{name}</span>
        <div className="flex gap-1 shrink-0">
          {special ? (
            <span className="text-xs font-bold text-amber-400">{win ? special : ''}</span>
          ) : (m.sets || []).map((s, i) => {
            const mine = side === 1 ? (s.player1_games ?? 0) : (s.player2_games ?? 0);
            const opp = side === 1 ? (s.player2_games ?? 0) : (s.player1_games ?? 0);
            return (
              <span key={i} className={`w-6 h-6 flex items-center justify-center rounded font-mono text-xs ${mine > opp ? 'bg-emerald-500 text-white font-bold' : 'bg-slate-700 text-slate-400'}`}>{mine}</span>
            );
          })}
        </div>
      </div>
    );
    return (
      <div className="replay-match rounded-xl border border-slate-700/80 bg-slate-800/60 backdrop-blur p-1.5 space-y-0.5" style={{ animationDelay: `${delay}ms` }}>
        <Row id={m.player1_id} name={m.player1_name} win={p1win} side={1} />
        <Row id={m.player2_id} name={m.player2_name} win={!p1win} side={2} />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col" dir="rtl" data-testid="tournament-replay">
      <style>{`
        @keyframes replayFadeUp { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes replayZoom { 0% { opacity: 0; transform: scale(0.85); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes replayProgress { 0% { width: 0%; } 100% { width: 100%; } }
        @keyframes confettiFall { 0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; } 100% { transform: translateY(110vh) rotate(720deg); opacity: 0.4; } }
        .replay-match { animation: replayFadeUp 0.55s ease-out both; }
        .replay-title { animation: replayZoom 0.6s ease-out both; }
        .replay-confetti { position: absolute; top: 0; width: 10px; height: 16px; border-radius: 2px; animation: confettiFall linear infinite; }
      `}</style>

      {/* Progress segments */}
      <div className="flex gap-1 px-4 pt-4">
        {slides.map((_, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full bg-slate-700/70 overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full"
              style={i < idx ? { width: '100%' } : i === idx && playing ? { animation: `replayProgress ${SLIDE_MS}ms linear forwards` } : i === idx ? { width: '40%' } : { width: '0%' }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="text-slate-300 text-sm font-bold truncate">🎬 {tournamentName}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPlaying((p) => !p)} data-testid="replay-play-pause" className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 text-amber-300 hover:bg-slate-700 transition-colors">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button onClick={onClose} data-testid="replay-close" className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Slide content */}
      <div className="flex-1 overflow-y-auto flex items-center justify-center px-4 pb-4 relative">
        {slide.type === 'intro' && (
          <div key={idx} className="replay-title text-center max-w-lg">
            <div className="text-6xl mb-6">🎾</div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-amber-300 mb-3">{tournamentName}</h2>
            {tournamentDate && <p className="text-slate-400 mb-6">{new Date(tournamentDate).toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
            <p className="text-xl text-slate-200 font-bold">שחזור הטורניר מתחיל... 🍿</p>
          </div>
        )}

        {slide.type === 'round' && (
          <div key={idx} className="w-full max-w-xl">
            <h3 className="replay-title text-center text-2xl md:text-3xl font-extrabold text-amber-300 mb-6">
              {slide.title}{slide.part ? ` (${slide.part})` : ''}
            </h3>
            <div className="space-y-3">
              {slide.matches.map((m, i) => <MatchRow key={m.id} m={m} delay={300 + i * 350} />)}
            </div>
          </div>
        )}

        {slide.type === 'champion' && (
          <div key={idx} className="text-center max-w-lg relative">
            {[...Array(24)].map((_, i) => (
              <span
                key={i}
                className="replay-confetti"
                style={{
                  left: `${(i * 137) % 100}%`,
                  background: ['#fbbf24', '#34d399', '#60a5fa', '#f472b6', '#f87171'][i % 5],
                  animationDuration: `${2.5 + (i % 5) * 0.6}s`,
                  animationDelay: `${(i % 8) * 0.3}s`,
                }}
              />
            ))}
            <div className="replay-title">
              <div className="text-7xl mb-4">🏆</div>
              <p className="text-slate-400 text-lg mb-2">אלוף הטורניר</p>
              <div className="flex items-center justify-center gap-3 mb-4">
                <PlayerAvatar id={slide.champId} name={slide.champName} size="w-14 h-14" />
                <h2 className="text-3xl md:text-5xl font-extrabold text-amber-300" data-testid="replay-champion-name">{slide.champName}</h2>
              </div>
              {slide.runnerName && <p className="text-slate-300 mb-1">🥈 סגן אלוף: <strong>{slide.runnerName}</strong></p>}
              {slide.consChamp && <p className="text-slate-300">🎗️ אלוף התנחומים: <strong>{slide.consChamp}</strong></p>}
              <button
                onClick={() => handleShareVideo(slide)}
                disabled={videoProgress !== null}
                data-testid="share-champion-video-btn"
                className="mt-6 mx-auto flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 font-extrabold hover:from-amber-300 hover:to-yellow-400 disabled:opacity-70 transition-all shadow-lg shadow-amber-400/25"
              >
                <Clapperboard className="w-5 h-5" />
                {videoProgress !== null ? `מייצר סרטון... ${videoProgress}%` : 'שתף סרטון הכתרה 🎬'}
              </button>
              <p className="mt-4 text-slate-500 text-sm">כל הכבוד לכל המשתתפים! 👏</p>
            </div>
          </div>
        )}
      </div>

      {/* Nav controls */}
      <div className="flex items-center justify-center gap-6 pb-6">
        <button onClick={prev} disabled={idx === 0} data-testid="replay-prev" className="w-11 h-11 flex items-center justify-center rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-30 transition-colors">
          <ChevronRight className="w-6 h-6" />
        </button>
        <span className="text-slate-500 text-sm font-mono" dir="ltr">{idx + 1} / {total}</span>
        <button onClick={next} disabled={idx === total - 1} data-testid="replay-next" className="w-11 h-11 flex items-center justify-center rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-30 transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default TournamentReplay;
